import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GH } from '../theme';
import { useWindowWidth } from '../hooks/useWindowWidth';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Reveal } from '../components/Reveal';
import { SectionLabel } from '../components/SectionLabel';
import { GHButton } from '../components/GHButton';
import { ImagePlaceholder } from '../components/ImagePlaceholder';

const QUOTE_SERVICES = [
  { id: 'construction',  label: 'Construction Works'    },
  { id: 'maintenance',   label: 'Property Maintenance'  },
  { id: 'window',        label: 'Window Cleaning'        },
  { id: 'waterblast',    label: 'Water Blasting'         },
  { id: 'painting',      label: 'Building Painting'      },
  { id: 'carpet',        label: 'Carpet Cleaning'        },
  { id: 'exterior',      label: 'Exterior Cleaning'      },
  { id: 'residential',   label: 'Residential Services'  },
];

const BUDGETS = [
  'Under $1,000', '$1,000–$5,000', '$5,000–$20,000',
  '$20,000–$100,000', '$100,000+', 'Not sure yet',
];

const TIMELINES = [
  'ASAP (within a week)', 'Within a month',
  '1–3 months', '3–6 months', 'Just exploring',
];

const PROPERTY_TYPES = ['Commercial', 'Residential', 'Industrial', 'Mixed-use', 'Body Corporate'];

const TOTAL_STEPS = 6;

const STEPS = [
  { num: 1, label: 'Service'   },
  { num: 2, label: 'Property'  },
  { num: 3, label: 'Details'   },
  { num: 4, label: 'Timeline'  },
  { num: 5, label: 'Budget'    },
  { num: 6, label: 'Contact'   },
];

export default function QuotePage({ navigate }) {
  const w = useWindowWidth();
  const mob = w < 768;
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    services: [], propertyType: '', address: '', size: '',
    description: '', timeline: '', budget: '',
    name: '', company: '', email: '', phone: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [quoteRef, setQuoteRef] = useState('');
  const [attachments, setAttachments] = useState([]);

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const toggleService = (id) => set('services', data.services.includes(id) ? data.services.filter(s => s !== id) : [...data.services, id]);

  const canNext = () => {
    if (step === 1) return data.services.length > 0;
    if (step === 2) return data.propertyType !== '';
    if (step === 3) return data.description.trim().length > 10;
    if (step === 4) return data.timeline !== '';
    if (step === 5) return data.budget !== '';
    if (step === 6) return data.name && data.email;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const formData = new FormData();
      formData.append('services', JSON.stringify(data.services));
      formData.append('propertyType', data.propertyType);
      formData.append('timeline', data.timeline);
      formData.append('budget', data.budget);
      formData.append('description', data.description);
      formData.append('name', data.name);
      formData.append('company', data.company);
      formData.append('email', data.email);
      formData.append('phone', data.phone);
      formData.append('address', data.address);
      attachments.forEach(f => formData.append('attachments', f));
      const res = await fetch('/api/quote', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || 'Something went wrong.');
      }
      const result = await res.json();
      setQuoteRef(result.quoteRef || '');
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <QuoteConfirmation navigate={navigate} quoteRef={quoteRef} />;

  return (
    <div style={{ background: GH.colors.bg, minHeight: '100vh', padding: mob ? '100px 20px 60px' : '120px 40px 80px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <SectionLabel>Get a Quote</SectionLabel>
          <h1 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: 'clamp(32px, 4vw, 48px)', color: GH.colors.white, letterSpacing: '-0.03em', margin: '20px 0 0' }}>
            Tell Us About Your Project
          </h1>
        </div>

        {/* Step progress */}
        {!mob && <div style={{ display: 'flex', gap: 0, marginBottom: 56, position: 'relative' }}>
          <div style={{ position: 'absolute', top: 16, left: 0, right: 0, height: 1, background: GH.colors.borderDk, zIndex: 0 }} />
          {STEPS.map(s => (
            <StepIndicator key={s.num} num={s.num} label={s.label} current={step} total={TOTAL_STEPS} />
          ))}
        </div>}
        {mob && <div style={{ fontFamily: GH.fonts.body, fontSize: 13, color: GH.colors.mutedDk, textAlign: 'center', marginBottom: 32 }}>Step {step} of {TOTAL_STEPS}</div>}

        {/* Card */}
        <div style={{ background: GH.colors.card, border: `1px solid ${GH.colors.borderDk}`, borderRadius: 2, padding: mob ? '36px 24px' : '52px 52px' }}>
          {step === 1 && <Step1 data={data} toggleService={toggleService} />}
          {step === 2 && <Step2 data={data} set={set} />}
          {step === 3 && <Step3 data={data} set={set} attachments={attachments} setAttachments={setAttachments} />}
          {step === 4 && <Step4 data={data} set={set} />}
          {step === 5 && <Step5 data={data} set={set} />}
          {step === 6 && <Step6 data={data} set={set} />}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 28 }}>
          {step > 1 ? (
            <GHButton variant="ghost" onClick={() => setStep(s => s - 1)}>← Back</GHButton>
          ) : (
            <GHButton variant="ghost" onClick={() => navigate('home')}>← Cancel</GHButton>
          )}
          <div style={{ fontFamily: GH.fonts.body, fontSize: 13, color: GH.colors.mutedDk }}>Step {step} of {TOTAL_STEPS}</div>
          {step < TOTAL_STEPS ? (
            <GHButton disabled={!canNext()} onClick={() => setStep(s => s + 1)}>Continue →</GHButton>
          ) : (
            <>
              <GHButton disabled={!canNext() || submitting} onClick={handleSubmit}>{submitting ? 'Submitting...' : 'Submit Request'}</GHButton>
              {submitError && <div style={{ fontFamily: GH.fonts.body, fontSize: 13, color: GH.colors.error, marginTop: 12 }}>{submitError}</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StepIndicator({ num, label, current, total }) {
  const done    = num < current;
  const active  = num === current;
  const future  = num > current;
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, position: 'relative', zIndex: 1 }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        background: done ? GH.colors.accent : active ? GH.colors.accentMuted : GH.colors.card,
        border: `2px solid ${done || active ? GH.colors.accent : GH.colors.borderDk}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: GH.fonts.display, fontWeight: 700, fontSize: 12,
        color: done ? '#fff' : active ? GH.colors.accent : GH.colors.mutedDk,
        transition: 'all 0.3s ease',
      }}>{done ? '✓' : num}</div>
      <span style={{ fontFamily: GH.fonts.body, fontSize: 11, color: active ? GH.colors.white : GH.colors.mutedDk, letterSpacing: '0.04em', fontWeight: active ? 600 : 400 }}>{label}</span>
    </div>
  );
}

function StepTitle({ title, sub }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <h2 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: 24, color: GH.colors.white, letterSpacing: '-0.02em', margin: '0 0 8px' }}>{title}</h2>
      {sub && <p style={{ fontFamily: GH.fonts.body, fontSize: 14, color: GH.colors.mutedDk, margin: 0 }}>{sub}</p>}
    </div>
  );
}

function Step1({ data, toggleService }) {
  const w = useWindowWidth();
  const mob = w < 768;
  return (
    <div>
      <StepTitle title="Which services do you need?" sub="Select all that apply." />
      <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr 1fr' : '1fr 1fr 1fr', gap: 10 }}>
        {QUOTE_SERVICES.map(s => {
          const sel = data.services.includes(s.id);
          return (
            <div key={s.id} onClick={() => toggleService(s.id)} style={{
              padding: '16px 18px', borderRadius: 2, cursor: 'pointer',
              background: sel ? GH.colors.accentMuted : 'rgba(22,28,42,0.025)',
              border: `1.5px solid ${sel ? GH.colors.accent : GH.colors.borderDk}`,
              transition: 'all 0.2s ease',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 16, height: 16, borderRadius: 2, border: `1.5px solid ${sel ? GH.colors.accent : GH.colors.borderMd}`, background: sel ? GH.colors.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 10, color: '#fff' }}>{sel ? '✓' : ''}</div>
                <span style={{ fontFamily: GH.fonts.body, fontSize: 13, fontWeight: 500, color: sel ? GH.colors.white : GH.colors.subDk }}>{s.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Step2({ data, set }) {
  const inputS = { width: '100%', boxSizing: 'border-box', background: 'rgba(22,28,42,0.03)', border: `1px solid ${GH.colors.borderDk}`, borderRadius: 2, padding: '13px 16px', fontFamily: GH.fonts.body, fontSize: 14, color: GH.colors.white, outline: 'none' };
  return (
    <div>
      <StepTitle title="About the property" sub="Help us understand the scope of your project." />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div>
          <label style={{ fontFamily: GH.fonts.body, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: GH.colors.greyLight, display: 'block', marginBottom: 8 }}>Property Type</label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {PROPERTY_TYPES.map(pt => {
              const sel = data.propertyType === pt;
              return (
                <div key={pt} onClick={() => set('propertyType', pt)} style={{
                  padding: '10px 20px', borderRadius: 2, cursor: 'pointer', fontFamily: GH.fonts.body, fontSize: 13, fontWeight: 500,
                  background: sel ? GH.colors.accentMuted : 'rgba(22,28,42,0.025)',
                  border: `1.5px solid ${sel ? GH.colors.accent : GH.colors.borderDk}`,
                  color: sel ? GH.colors.white : GH.colors.subDk, transition: 'all 0.2s',
                }}>{pt}</div>
              );
            })}
          </div>
        </div>
        <div>
          <label style={{ fontFamily: GH.fonts.body, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: GH.colors.greyLight, display: 'block', marginBottom: 8 }}>Property Address / Location</label>
          <input value={data.address} onChange={e => set('address', e.target.value)} placeholder="e.g. 123 Queen Street, Auckland CBD" style={inputS} />
        </div>
        <div>
          <label style={{ fontFamily: GH.fonts.body, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: GH.colors.greyLight, display: 'block', marginBottom: 8 }}>Approximate Size (m² or levels)</label>
          <input value={data.size} onChange={e => set('size', e.target.value)} placeholder="e.g. 2,500m² / 8-storey building" style={inputS} />
        </div>
      </div>
    </div>
  );
}

function Step3({ data, set, attachments, setAttachments }) {
  const fileRef = useRef(null);
  const [attachError, setAttachError] = useState('');
  const ALLOWED_EXT = ['.jpg','.jpeg','.png','.webp','.gif','.pdf'];
  const MAX_SIZE = 5 * 1024 * 1024;
  const MAX_FILES = 5;
  const handleFiles = (e) => {
    setAttachError('');
    const incoming = Array.from(e.target.files || []);
    const combined = [...attachments];
    let err = '';
    for (const f of incoming) {
      if (combined.length >= MAX_FILES) { err = `Maximum ${MAX_FILES} attachments allowed.`; break; }
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) { err = 'Only JPG, PNG, WebP, GIF and PDF files are accepted.'; break; }
      if (f.size > MAX_SIZE) { err = 'File exceeds 5 MB limit.'; break; }
      combined.push(f);
    }
    if (err) setAttachError(err);
    else setAttachments(combined);
    e.target.value = '';
  };
  const removeFile = (i) => setAttachments(a => a.filter((_, idx) => idx !== i));
  return (
    <div>
      <StepTitle title="Describe your project" sub="The more detail you provide, the more accurate our quote will be." />
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontFamily: GH.fonts.body, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: GH.colors.greyLight, display: 'block', marginBottom: 8 }}>Project Description *</label>
        <textarea value={data.description} onChange={e => set('description', e.target.value)}
          placeholder="Describe what you need done, any specific requirements, access considerations, or other relevant details\u2026"
          rows={7} style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(22,28,42,0.03)', border: `1px solid ${GH.colors.borderDk}`, borderRadius: 2, padding: '14px 18px', fontFamily: GH.fonts.body, fontSize: 14, color: GH.colors.white, outline: 'none', resize: 'vertical', minHeight: 160 }} />
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontFamily: GH.fonts.body, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: GH.colors.greyLight, display: 'block', marginBottom: 8 }}>Attachments <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: GH.colors.mutedDk }}>(optional: photos, plans, PDFs)</span></label>
        <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.gif,.pdf" onChange={handleFiles} style={{ display: 'none' }} />
        <div onClick={() => fileRef.current?.click()} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); handleFiles({ target: { files: e.dataTransfer.files } }); }}
          style={{ border: `2px dashed ${attachError ? '#dc2626' : GH.colors.borderMd}`, borderRadius: 2, padding: '20px 18px', textAlign: 'center', cursor: 'pointer', background: 'rgba(22,28,42,0.02)', transition: 'border-color 0.2s' }}
          onMouseEnter={e => { if (!attachError) e.currentTarget.style.borderColor = GH.colors.accent; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = attachError ? '#dc2626' : GH.colors.borderMd; }}>
          <div style={{ fontFamily: GH.fonts.body, fontSize: 14, color: GH.colors.mutedDk }}>Click or drag to attach files</div>
          <div style={{ fontFamily: GH.fonts.body, fontSize: 11, color: GH.colors.grey, marginTop: 6 }}>JPG \u00b7 PNG \u00b7 PDF \u00b7 max 5 MB each \u00b7 up to 5 files</div>
        </div>
        {attachError && <div style={{ fontFamily: GH.fonts.body, fontSize: 12, color: '#dc2626', marginTop: 8 }}>\u26a0 {attachError}</div>}
        {attachments.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {attachments.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: GH.colors.accentMuted, border: `1px solid ${GH.colors.accent}33`, borderRadius: 2, padding: '8px 14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontFamily: GH.fonts.body, fontSize: 13, color: GH.colors.white }}>{f.name}</span>
                  <span style={{ fontFamily: GH.fonts.body, fontSize: 11, color: GH.colors.mutedDk }}>({(f.size / 1024).toFixed(0)} KB)</span>
                </div>
                <button onClick={(e) => { e.stopPropagation(); removeFile(i); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: GH.colors.mutedDk, fontSize: 16, lineHeight: 1, padding: '0 4px' }} title="Remove">\u00d7</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Step4({ data, set }) {
  return (
    <div>
      <StepTitle title="What's your timeline?" sub="When do you need the work completed?" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {TIMELINES.map(t => {
          const sel = data.timeline === t;
          return (
            <div key={t} onClick={() => set('timeline', t)} style={{
              padding: '18px 24px', borderRadius: 2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14,
              background: sel ? GH.colors.accentMuted : 'rgba(22,28,42,0.025)',
              border: `1.5px solid ${sel ? GH.colors.accent : GH.colors.borderDk}`,
              transition: 'all 0.2s',
            }}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${sel ? GH.colors.accent : GH.colors.borderMd}`, background: sel ? GH.colors.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {sel && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
              </div>
              <span style={{ fontFamily: GH.fonts.body, fontSize: 14, fontWeight: sel ? 600 : 400, color: sel ? GH.colors.white : GH.colors.subDk }}>{t}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Step5({ data, set }) {
  return (
    <div>
      <StepTitle title="What's your budget range?" sub="This helps us tailor our proposal to your requirements." />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {BUDGETS.map(b => {
          const sel = data.budget === b;
          return (
            <div key={b} onClick={() => set('budget', b)} style={{
              padding: '20px 24px', borderRadius: 2, cursor: 'pointer',
              background: sel ? GH.colors.accentMuted : 'rgba(22,28,42,0.025)',
              border: `1.5px solid ${sel ? GH.colors.accent : GH.colors.borderDk}`,
              transition: 'all 0.2s',
            }}>
              <div style={{ fontFamily: GH.fonts.display, fontWeight: 700, fontSize: 15, color: sel ? GH.colors.white : GH.colors.subDk, letterSpacing: '-0.01em' }}>{b}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Step6({ data, set }) {
  const w = useWindowWidth();
  const mob = w < 768;
  const inputS = { width: '100%', boxSizing: 'border-box', background: 'rgba(22,28,42,0.03)', border: `1px solid ${GH.colors.borderDk}`, borderRadius: 2, padding: '13px 16px', fontFamily: GH.fonts.body, fontSize: 14, color: GH.colors.white, outline: 'none' };
  const labelS = { fontFamily: GH.fonts.body, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: GH.colors.greyLight, display: 'block', marginBottom: 8 };
  return (
    <div>
      <StepTitle title="Your contact details" sub="How should we reach you with your quote?" />
      <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div>
          <label style={labelS}>Full Name *</label>
          <input required value={data.name} onChange={e => set('name', e.target.value)} placeholder="John Smith" style={inputS} />
        </div>
        <div>
          <label style={labelS}>Company</label>
          <input value={data.company} onChange={e => set('company', e.target.value)} placeholder="Optional" style={inputS} />
        </div>
        <div>
          <label style={labelS}>Email *</label>
          <input required type="email" value={data.email} onChange={e => set('email', e.target.value)} placeholder="john@company.com" style={inputS} />
        </div>
        <div>
          <label style={labelS}>Phone</label>
          <input value={data.phone} onChange={e => set('phone', e.target.value)} placeholder="+64 21 000 000" style={inputS} />
        </div>
      </div>
      <div style={{ background: GH.colors.card, border: `1px solid ${GH.colors.borderDk}`, borderRadius: 2, padding: '20px 24px' }}>
        <div style={{ fontFamily: GH.fonts.display, fontWeight: 700, fontSize: 14, color: GH.colors.white, marginBottom: 12 }}>Your Request Summary</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: GH.fonts.body, fontSize: 13, color: GH.colors.mutedDk }}>
          <span><strong style={{ color: GH.colors.subDk }}>Services:</strong> {data.services.join(', ') || '—'}</span>
          <span><strong style={{ color: GH.colors.subDk }}>Property:</strong> {[data.propertyType, data.address].filter(Boolean).join(' — ') || '—'}</span>
          <span><strong style={{ color: GH.colors.subDk }}>Timeline:</strong> {data.timeline || '—'}</span>
          <span><strong style={{ color: GH.colors.subDk }}>Budget:</strong> {data.budget || '—'}</span>
        </div>
      </div>
    </div>
  );
}

function QuoteConfirmation({ navigate, quoteRef }) {
  return (
    <div style={{ background: GH.colors.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '120px 40px' }}>
      <div style={{ textAlign: 'center', maxWidth: 540 }}>
        <div style={{ width: 72, height: 72, background: GH.colors.accentMuted, border: `2px solid ${GH.colors.accent}`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 32px', fontSize: 28, color: GH.colors.accent }}>✓</div>
        <h1 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: 36, color: GH.colors.white, letterSpacing: '-0.03em', margin: '0 0 16px' }}>Quote Request Received</h1>
        {quoteRef && <p style={{ fontFamily: GH.fonts.body, fontSize: 14, color: GH.colors.gold, fontWeight: 600, letterSpacing: '0.06em', margin: '0 0 20px' }}>Reference: {quoteRef}</p>}
        <p style={{ fontFamily: GH.fonts.body, fontSize: 16, color: GH.colors.mutedDk, lineHeight: 1.7, margin: '0 0 16px' }}>
          Thanks for reaching out to GreyHolm Group. Our team will review your request and get back to you as soon as possible.
        </p>
        <p style={{ fontFamily: GH.fonts.body, fontSize: 14, color: GH.colors.mutedDk, lineHeight: 1.7, margin: '0 0 40px' }}>
          For urgent works, call us directly: <strong style={{ color: GH.colors.white }}>020 4198 1876</strong>
        </p>
        <GHButton size="lg" onClick={() => navigate('home')}>Back to Home</GHButton>
      </div>
    </div>
  );
}

